import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { timeFormatSelector } from 'features/events/eventSlice'
import { MeetingOrder, MeetingType } from 'generated/graphql'
import { InfoItem } from 'components'
import DataGrid, { ColumnDataGrid } from 'components/DataGrid'
import { TopToolbar } from 'components/lists'
import { Pagination } from 'features/pagination'
import { useDeleteItem, useEventSlug, useSlug } from 'hooks'
import styled, { css } from 'styled-components'
import { colors, _ } from 'utils'
import PlaceholderUser from 'assets/placeholder-user.png'
import Icon from 'components/Icon'
import { getNodes } from 'graphql/utils'
import { format } from 'date-fns'
import routes from 'routes'
import { useMeetings } from '../hooks'
import { deleteMeeting } from '../graphql/mutation'
import {
    handleSubmit as handleSubmitMeeting, importProps as importMeetingProps
} from '../import_configs/meetingConfig'
import {
    handleSubmit as handleSubmitParticipant, importProps as importParticipantsProps
} from '../import_configs/participantsConfig'

const S = {
    Wrapper: styled.div`
        width: 100%;
        margin-top: -28px;
        padding: 0 10px;
    `,
    InfoItem: styled(InfoItem)<{ numberOfHosts: number }>`
        padding: 0;
        & .info-image {
            width: 40px;
            height: 40px;
        }
        ${({ numberOfHosts }) => numberOfHosts > 1 && css<{ numberOfHosts: number }>`
            &:after {
                content: ${() => `'+${numberOfHosts-1}'`};
                position: absolute;
                top: 0;
                left: 30px;
                display: flex;
                align-items: center;
                width: 20px;
                height: 40px;
                border-bottom-right-radius: 40px;
                border-top-right-radius: 40px;
                background-color:  rgba(0, 0, 0, 0.5);
                font-size: 12px;
                font-weight: 700;
                color: white;
            }
        `}
    `,
    Text: styled.span`
        display: flex;
        align-items: center;
        color: ${colors.icon};
        font-size: 14px;
    `,
    NoHost: styled.span`
        white-space: nowrap;
        color: ${colors.icon};
        font-size: 14px;
        font-weight: 500;
        opacity: 0.5;
        text-transform: uppercase;
    `,
}

const getHostNames = (row: MeetingType) => {
    const hosts = getNodes(row.hosts)
    return hosts.map((host) => {
        if (!host.user?.firstName) {
            return host.user?.email
        }
        return `${host.user?.firstName} ${host.user?.lastName}`
    })
}

const formatDate = (startDate, endDate, timeFormat) => (
    `${format(startDate, 'dd/MM/yyy')} | ${format(startDate, timeFormat)}-${format(endDate, timeFormat)}`
)

const Meetings = ({ spaceId }: { spaceId: number }) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const meetingSlug = useSlug('meetingSlug')
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState(MeetingOrder.StartsAtAsc)
    const [dateFilter, setDateFilter] = useState(null)
    const history = useHistory()
    const timeFormat = useSelector(timeFormatSelector)

    const goToEditPage = (row) => (
        history.push(routes.updateMeeting.getPath({
            eventSlug, meetingSlug, meetingId: row.id
        }), { spaceId })
    )

    const {
        list, requestStatus, pagination, totalItems, handlePageChange, fetchData
    } = useMeetings({
        eventId: eventSlug,
        search,
        sort,
        date: dateFilter && format(dateFilter, 'yyyy-MM-dd'),
        spaceFilters: { parent: { slug: meetingSlug } },
    })

    const deleteFn = (id) => deleteMeeting(id)
    const { showDeleteConfirmModal } = useDeleteItem(deleteFn, fetchData)

    const columns: ColumnDataGrid[] = useMemo(() => [
        {
            name: 'image',
            width: 5,
            minWidth: 200,
            render: (row: MeetingType) => (
                <S.InfoItem
                    image={getNodes(row.hosts)?.[0]?.user?.mugshot || PlaceholderUser}
                    name={row.title}
                    description={getHostNames(row).join(', ')}
                    numberOfHosts={getNodes(row.hosts).length}
                />
            )
        },
        {
            name: 'date',
            width: 4,
            render: (row: MeetingType) => (
                <S.Text>
                    <Icon icon="calendar" size={18} style={{ margin: '0 4px 3px 0' }} />
                    {row.startsAt && formatDate(new Date(row.startsAt), new Date(row.endsAt), timeFormat)}
                </S.Text>
            )
        },
        {
            name: 'capacity',
            width: 1,
            render: (row: MeetingType) => (
                <S.Text>
                    <Icon icon="group" size={18} style={{ margin: '0 4px 3px 0' }} />
                    {`${row.capacity - row.capacityLeft}/${row.capacity}`}
                </S.Text>
            )
        },
        {
            name: 'noHostIndication',
            width: 1,
            render: (row: MeetingType) => (
                <S.NoHost>
                    {!row?.hosts?.totalCount ? _('meeting.no_host') : ''}
                </S.NoHost>
            )
        },
        {
            name: 'actions',
            width: 1,
            render: (row: MeetingType) => (
                <>
                    <Icon icon="trash" size={18} onClick={() => showDeleteConfirmModal(row.id)} />
                </>
            )
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [])

    return (
        <S.Wrapper>
            <TopToolbar
                addItemText={_('meeting.list.add_item_button')}
                onAddItem={() => history.push(routes.createMeeting.getPath({
                    eventSlug, meetingSlug
                }), { spaceId })}
                onSearch={setSearch}
                searchPlaceHolder={_('meeting.list.search_placeholder')}
                sortOptions={[
                    { label: _('meeting.form.sort_by.start_date_asc'), value: MeetingOrder.StartsAtAsc },
                    { label: _('meeting.form.sort_by.start_date_desc'), value: MeetingOrder.StartsAtDesc },
                    { label: _('meeting.form.sort_by.title_asc'), value: MeetingOrder.TitleAsc },
                    { label: _('meeting.form.sort_by.title_desc'), value: MeetingOrder.TitleDesc },
                    {
                        label: _('meeting.form.sort_by.total_capacity_asc'),
                        value: MeetingOrder.TotalCapacityAsc
                    },
                    {
                        label: _('meeting.form.sort_by.total_capacity_desc'),
                        value: MeetingOrder.TotalCapacityDesc
                    },
                    {
                        label: _('meeting.form.sort_by.capacity_left_asc'),
                        value: MeetingOrder.CapacityLeftAsc
                    },
                    {
                        label: _('meeting.form.sort_by.capacity_left_desc'),
                        value: MeetingOrder.CapacityLeftDesc
                    },
                ]}
                onSort={setSort}
                dateFilter={{
                    placeHolder: _('meeting.form.date_filter.place_holder'),
                    onChangeDate: setDateFilter,
                }}
                bulkActions={{
                    multipleImport: [
                        {
                            title: _('meeting.import_meetings'),
                            ...importMeetingProps,
                            onSubmit: (data) => handleSubmitMeeting(spaceId, data, dispatch, fetchData)
                        },
                        {
                            title: _('meeting.import_meeting_participants'),
                            ...importParticipantsProps,
                            onSubmit: (data) => handleSubmitParticipant(spaceId, data, dispatch, fetchData)
                        }
                    ]
                }}
            />
            <>
                <DataGrid
                    columns={columns}
                    data={list}
                    hideAction
                    onRowClick={(row) => goToEditPage(row)}
                    requestStatus={requestStatus}
                />
                <Pagination
                    page={pagination?.page}
                    onPageChange={handlePageChange}
                    totalItems={totalItems}
                    disabled={requestStatus === 'loading'}
                />
            </>
        </S.Wrapper>
    )
}

export default Meetings
